import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Snackbar,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import FileSearch from "components/fileStorage/FileSearch";
import PaginationControls from "components/fileStorage/PaginationControls";
import UserManagementPanel, { UserManagementDrafts } from "components/userManagement/UserManagementPanel";
import UserManagementTable from "components/userManagement/UserManagementTable";
import BackendUserTable from "components/userManagement/BackendUserTable";
import BackendUserPanel from "components/userManagement/BackendUserPanel";
import {
  GetAdminUsersParams,
  useGetAdminUsersQuery,
  useGetBackendUsersQuery,
} from "features/adminUsers/adminUsers";
import { useAppSelector } from "features/store";
import { useEffect, useRef, useState } from "react";
import { BackendUser, PlatformUser } from "types/interfaces/adminUsers";
import Layout from "../components/Layout";

const DEFAULT_PAGE_SIZE = 50;
const MODE_STORAGE_KEY = "userManagementMode";
const BACKEND_SELECTED_KEY = "userManagementSelectedBackendId";
const PLATFORM_SELECTED_KEY = "userManagementSelectedPlatformId";
const DRAFTS_STORAGE_KEY = "userManagementPlatformDrafts";

function loadDraftsFromStorage(): Record<number, UserManagementDrafts> {
  try {
    const raw = window.localStorage.getItem(DRAFTS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, UserManagementDrafts>;
    const result: Record<number, UserManagementDrafts> = {};
    for (const [k, v] of Object.entries(parsed)) {
      const id = parseInt(k, 10);
      if (!Number.isNaN(id) && v && typeof v === "object") {
        result[id] = v;
      }
    }
    return result;
  } catch {
    return {};
  }
}

function saveDraftsToStorage(drafts: Record<number, UserManagementDrafts>) {
  try {
    window.localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));
  } catch {
    // ignore storage errors
  }
}

export default function UserManagementPage() {
  const currentUser = useAppSelector((state) => state.auth.user);
  const pp = currentUser?.platform_profile;
  const canCreateUsers = Boolean(pp?.is_superuser);
  const isSuperUser = Boolean(pp?.is_superuser);
  const [mode, setMode] = useState<"web" | "backend">("web");
  const [sortBy, setSortBy] = useState<GetAdminUsersParams["sort_by"] | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<GetAdminUsersParams["sort_order"]>("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
  const [backendLabelFilter, setBackendLabelFilter] = useState<"migration" | "integration" | undefined>(undefined);
  const [createMode, setCreateMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const createdUserRef = useRef<PlatformUser | null>(null);
  const createdAtRef = useRef<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, UserManagementDrafts>>(loadDraftsFromStorage);
  const [isSaving, setIsSaving] = useState(false);

  // Restore mode from localStorage for Super Admins
  useEffect(() => {
    if (!isSuperUser) return;
    try {
      const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
      if (stored === "backend" || stored === "web") {
        setMode(stored);
      }
    } catch {
      // ignore storage errors
    }
  }, [isSuperUser]);

  // Restore selected backend user when mode is backend
  useEffect(() => {
    if (mode !== "backend") return;
    try {
      const raw = window.localStorage.getItem(BACKEND_SELECTED_KEY);
      const id = raw ? parseInt(raw, 10) : NaN;
      if (!Number.isNaN(id)) {
        setSelectedUserId(id);
      }
    } catch {
      // ignore storage errors
    }
  }, [mode]);

  // Restore selected platform user when mode is web
  useEffect(() => {
    if (mode !== "web") return;
    try {
      const raw = window.localStorage.getItem(PLATFORM_SELECTED_KEY);
      const id = raw ? parseInt(raw, 10) : NaN;
      if (!Number.isNaN(id)) {
        setSelectedUserId(id);
      }
    } catch {
      // ignore storage errors
    }
  }, [mode]);

  const { data, isLoading, isFetching } = useGetAdminUsersQuery(
    {
      page,
      search: search || undefined,
      page_size: pageSize,
      role: roleFilter,
      ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
    },
    { refetchOnMountOrArgChange: true },
  );

  const users = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  const {
    data: backendData,
    isLoading: isBackendLoading,
    isFetching: isBackendFetching,
  } = useGetBackendUsersQuery(
    {
      page,
      search: search || undefined,
      page_size: pageSize,
      ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
    },
    { skip: mode !== "backend" },
  );

  const backendUsersRaw = backendData?.results ?? [];
  const backendUsers =
    backendLabelFilter === "migration"
      ? backendUsersRaw.filter((u) => u.folder_count > 0)
      : backendLabelFilter === "integration"
        ? backendUsersRaw.filter((u) => u.integration_count > 0)
        : backendUsersRaw;
  const backendTotalItems = backendData?.count ?? 0;
  const backendTotalPages = Math.ceil(backendTotalItems / pageSize) || 1;

  const handleManagePlatform = (user: PlatformUser) => {
    if (
      createdUserRef.current &&
      user.id !== createdUserRef.current.id &&
      createdAtRef.current != null &&
      Date.now() - createdAtRef.current < 1500
    ) {
      return;
    }
    createdUserRef.current = null;
    createdAtRef.current = null;
    setSelectedUserId((prev) => {
      const next = prev === user.id ? null : user.id;
      try {
        if (next == null) {
          window.localStorage.removeItem(PLATFORM_SELECTED_KEY);
        } else {
          window.localStorage.setItem(PLATFORM_SELECTED_KEY, String(next));
        }
      } catch {
        // ignore storage errors
      }
      return next;
    });
    setCreateMode(false);
  };

  const handleManageBackend = (user: BackendUser) => {
    setSelectedUserId((prev) => {
      const next = prev === user.id ? null : user.id;
      try {
        if (next == null) {
          window.localStorage.removeItem(BACKEND_SELECTED_KEY);
        } else {
          window.localStorage.setItem(BACKEND_SELECTED_KEY, String(next));
        }
      } catch {
        // ignore storage errors
      }
      return next;
    });
    setCreateMode(false);
  };

  const panelUser: PlatformUser | null =
    createMode
      ? null
      : selectedUserId == null
        ? null
        : (users.find((u) => u.id === selectedUserId) ?? createdUserRef.current ?? null);
  const panelOpen = createMode || selectedUserId !== null;

  const showToast = (message: string, severity: "success" | "error") => {
    setToast({ open: true, message, severity });
  };

  const handleUserArchived = () => {
    showToast("User archive status updated.", "success");
  };

  const handleSortChange = (field: GetAdminUsersParams["sort_by"]) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortBy(undefined);
        setSortOrder("asc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  return (
    <Layout title="User Management" content="User Management">
      <Container
        component="main"
        maxWidth={false}
        sx={{
          py: 3,
          px: { xs: 1.5, sm: 3 },
          height: "calc(100vh - 200px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box mb={2} display="flex" flexDirection="column" gap={1}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box sx={{ minWidth: { xs: 0, sm: 220 }, maxWidth: 400 }}>
              <FileSearch
                onSearch={(term) => {
                  setSearch(term || "");
                  setPage(1);
                }}
                placeholder="Search users..."
                autoComplete="off"
              />
            </Box>
            <Tooltip title={!canCreateUsers ? "Super User required to create users." : ""}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (!canCreateUsers) return;
                    createdUserRef.current = null;
                    createdAtRef.current = null;
                    setCreateMode(true);
                    setSelectedUserId(null);
                  }}
                  disabled={!canCreateUsers}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  + New User
                </Button>
              </span>
            </Tooltip>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
              }}
            >
              {isSuperUser && (
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(_, value) => {
                    if (!value) return;
                    setMode(value);
                    try {
                      window.localStorage.setItem(MODE_STORAGE_KEY, value);
                    } catch {
                      // ignore storage errors
                    }
                    setSelectedUserId(null);
                    setCreateMode(false);
                    setPage(1);
                    setRoleFilter(undefined);
                    setBackendLabelFilter(undefined);
                  }}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <ToggleButton value="web" sx={{ textTransform: "none", fontWeight: 600 }}>
                    Web Users
                  </ToggleButton>
                  <ToggleButton value="backend" sx={{ textTransform: "none", fontWeight: 600 }}>
                    Backend Users
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {mode === "web"
              ? ([
                  { value: "data_storage", label: "Data Storage" },
                  { value: "integration_dashboard", label: "Integration" },
                  { value: "ai_agent", label: "AI Agent" },
                  { value: "calibrate_manager", label: "Manager" },
                  { value: "superuser", label: "Super Admin" },
                  { value: "archived", label: "Archived" },
                ] as const
                ).map(({ value, label }) => (
                  <Chip
                    key={value}
                    label={label}
                    size="small"
                    clickable
                    onClick={() => {
                      setRoleFilter((prev) => (prev === value ? undefined : value));
                      setPage(1);
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      bgcolor: roleFilter === value ? "primary.main" : undefined,
                      color: roleFilter === value ? "primary.contrastText" : undefined,
                      "& .MuiChip-label": { px: 1.25 },
                    }}
                  />
                ))
              : ([
                  { key: "migration" as const, label: "Migration" },
                  { key: "integration" as const, label: "Integration" },
                ] as const
                ).map(({ key, label }) => (
                  <Chip
                    key={key}
                    label={label}
                    size="small"
                    clickable
                    onClick={() => {
                      setBackendLabelFilter((prev) => (prev === key ? undefined : key));
                      setPage(1);
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      bgcolor: backendLabelFilter === key ? "primary.main" : undefined,
                      color: backendLabelFilter === key ? "primary.contrastText" : undefined,
                      "& .MuiChip-label": { px: 1.25 },
                    }}
                  />
                ))}
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            display: "flex",
            flex: 1,
            minHeight: 0,
            borderRadius: 2,
            boxShadow: 1,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: "background.paper",
          }}
        >
          <Backdrop
            open={isSaving}
            sx={{
              position: "absolute",
              zIndex: 1300,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.85)",
            }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
              <CircularProgress sx={{ color: "primary.main" }} />
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                Saving…
              </Typography>
            </Box>
          </Backdrop>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: { xs: panelOpen ? "none" : "flex", sm: "flex" },
              flexDirection: "column",
            }}
          >
            {mode === "web" ? (
              <UserManagementTable
                users={users}
                isLoading={isLoading}
                isFetching={isFetching}
                selectedUserId={selectedUserId ?? undefined}
                onManage={handleManagePlatform}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                hasUnsavedChangesForUser={(userId) => {
                  const d = drafts[userId];
                  if (!d) return false;
                  return Boolean(d.folders || d.integrations || d.chatbot || d.permissions || d.profile);
                }}
                pagination={
                  totalItems > 0 ? (
                    <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                      <PaginationControls
                        currentLimit={pageSize}
                        onLimitChange={(limit) => {
                          setPageSize(limit);
                          setPage(1);
                        }}
                        totalItems={totalItems}
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        showLimitSelector={true}
                        showPageInfo={true}
                        disabled={isFetching}
                        size="small"
                        pageTokens={[]}
                        hasNextPage={page < totalPages}
                        borderStyle={{ borderTop: "none" }}
                      />
                    </Box>
                  ) : null
                }
              />
            ) : (
              <BackendUserTable
                users={backendUsers}
                isLoading={isBackendLoading}
                isFetching={isBackendFetching}
                selectedUserId={selectedUserId ?? undefined}
                onManage={handleManageBackend}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                pagination={
                  backendTotalItems > 0 ? (
                    <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                      <PaginationControls
                        currentLimit={pageSize}
                        onLimitChange={(limit) => {
                          setPageSize(limit);
                          setPage(1);
                        }}
                        totalItems={backendTotalItems}
                        currentPage={page}
                        totalPages={backendTotalPages}
                        onPageChange={setPage}
                        showLimitSelector={true}
                        showPageInfo={true}
                        disabled={isBackendFetching}
                        size="small"
                        pageTokens={[]}
                        hasNextPage={page < backendTotalPages}
                        borderStyle={{ borderTop: "none" }}
                      />
                    </Box>
                  ) : null
                }
              />
            )}
          </Box>

          {panelOpen &&
            (mode === "web" ? (
              <UserManagementPanel
                user={panelUser}
                onClose={() => {
                  setCreateMode(false);
                  setSelectedUserId(null);
                  createdUserRef.current = null;
                  createdAtRef.current = null;
                  try {
                    window.localStorage.removeItem(PLATFORM_SELECTED_KEY);
                  } catch {
                    // ignore storage errors
                  }
                }}
                onUserUpdated={(updated) => {
                  createdUserRef.current = updated;
                  createdAtRef.current = Date.now();
                  setSelectedUserId(updated.id);
                  setCreateMode(false);
                  try {
                    window.localStorage.setItem(
                      PLATFORM_SELECTED_KEY,
                      String(updated.id),
                    );
                  } catch {
                    // ignore storage errors
                  }
                }}
                onUserArchived={handleUserArchived}
                onSavingChange={setIsSaving}
                drafts={drafts}
                onDraftsChange={(updater) => {
                  setDrafts((prev) => {
                    const next = updater(prev);
                    saveDraftsToStorage(next);
                    return next;
                  });
                }}
              />
            ) : (
              <BackendUserPanel
                user={
                  selectedUserId == null
                    ? null
                    : backendUsers.find((u) => u.id === selectedUserId) ?? null
                }
                onClose={() => {
                  setCreateMode(false);
                  setSelectedUserId(null);
                  try {
                    window.localStorage.removeItem(BACKEND_SELECTED_KEY);
                  } catch {
                    // ignore storage errors
                  }
                }}
                onUserUpdated={(updated) => {
                  setSelectedUserId(updated.id);
                  try {
                    window.localStorage.setItem(
                      BACKEND_SELECTED_KEY,
                      String(updated.id),
                    );
                  } catch {
                    // ignore storage errors
                  }
                }}
                onUserDeleted={() => {
                  setToast({
                    open: true,
                    message: "User deleted. S3 cleanup has been queued.",
                    severity: "success",
                  });
                }}
              />
            ))}
        </Box>
        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={toast.severity}
            onClose={() => setToast((t) => ({ ...t, open: false }))}
            sx={{ fontWeight: 600 }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
}
